import React, { useCallback, useEffect, useState } from 'react';

import { LoadingType } from 'actions';
import { setDashboardStep } from 'actions/stages';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { getApplicationInfoForDashboardThunk } from 'thunks/getApplicationInfoForDashboard';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import { hilAutomatedOffersApi, OffersResponseDto } from 'api/AutomatedOffersApi';
import AcceptAutomatedOffer from 'pages/Dashboard/steps/AcceptOffer/AutomatedOffers/AcceptAutomatedOffer';
import { ApplicationStatus } from 'enums/ApplicationStatus';
import { OfferDto } from 'api/OffersApi';

const AcceptHilAutomatedOffer = () => {
  const dispatch = useAppDispatch();
  const isGlobalLoading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const [isLoading, setIsLoading] = useState(true);
  const { applicationId, variables, applicationStatus } = useAppSelector((state) => state.application);
  const { borrowerId } = useAppSelector((state) => state.borrower);
  const [loanOffers, setLoanOffers] = useState<OfferDto[]>([]);
  const [currentLoanOffer, setCurrentLoanOffer] = useState<OfferDto | null>(null);
  const [maxLoanAmount, setMaxLoanAmount] = useState<number>(0);
  const [minLoanAmount, setMinLoanAmount] = useState<number>(0);
  const [loanAmount, setLoanAmount] = useState<number>(variables!.loan_amount!);

  const updateLoanOffers = useCallback((loanOffersResponse: OffersResponseDto) => {
    setLoanOffers(loanOffersResponse.loanOffers);
    setMaxLoanAmount(loanOffersResponse.maxLoanAmount);
    setMinLoanAmount(loanOffersResponse.minLoanAmount);

    if (loanAmount > loanOffersResponse.maxLoanAmount) {
      setLoanAmount(loanOffersResponse.maxLoanAmount);
    }
  }, []);

  const handleApplicationRejected = async () => {
    await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
    dispatch(setDashboardStep(null));
  };

  const getLoanOffers = useCallback(async () => {
    try {
      setIsLoading(true);

      const loanOffersResponse = await hilAutomatedOffersApi.getOffers({
        applicationId: applicationId!,
        loanAmount: loanAmount,
      });

      if (loanOffersResponse.isApplicationRejected) {
        await handleApplicationRejected();
      } else {
        updateLoanOffers(loanOffersResponse);
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  }, [applicationId, dispatch, loanAmount, updateLoanOffers]);

  useEffect(() => {
    if (applicationStatus === ApplicationStatus.Pitched) {
      getLoanOffers();
    }
  }, [applicationStatus, getLoanOffers, loanAmount]);

  const handleDeclineOffer = async () => {
    let isSuccess = false;

    try {
      setIsLoading(true);

      await hilAutomatedOffersApi.declineOffer({
        applicationId: applicationId!,
      });

      isSuccess = true;
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }

    return isSuccess;
  };

  const handleCloseDeclineOfferThanksPopup = async () => {
    try {
      setIsLoading(true);
      await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
      dispatch(setDashboardStep(null));
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptOffer = async () => {
    try {
      setIsLoading(true);

      const result = await hilAutomatedOffersApi.acceptOffer({
        applicationId: applicationId!,
        loanAmount: loanAmount,
        term: currentLoanOffer!.term,
        lender: currentLoanOffer!.lender,
      });

      if (result.isApplicationRejected) {
        await handleApplicationRejected();
      } else {
        if (currentLoanOffer!.offerUrl) {
          window.open(currentLoanOffer!.offerUrl, '_self');
        } else {
          await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
          dispatch(setDashboardStep(null));
        }
      }
    } catch (error: any) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AcceptAutomatedOffer
        loanAmount={loanAmount}
        loanOffers={loanOffers}
        setLoanAmount={setLoanAmount}
        minLoanAmount={minLoanAmount}
        maxLoanAmount={maxLoanAmount}
        currentLoanOffer={currentLoanOffer}
        setCurrentLoanOffer={setCurrentLoanOffer}
        onCloseDeclineOfferThanksPopup={handleCloseDeclineOfferThanksPopup}
        onDeclineOffer={handleDeclineOffer}
        onAcceptOffer={handleAcceptOffer}
        isLoading={isLoading || isGlobalLoading}
      />
    </>
  );
};

export default AcceptHilAutomatedOffer;
